import React, { useState, useEffect } from 'react';

interface Widget {
    id: string;
    type: string;
    order: number;
    isVisible: boolean;
    settings: Record<string, any>;
    customCss?: string;
}

interface PageTheme {
    primaryColor: string;
    accentColor: string;
    font: string;
    layout: 'default' | 'minimal' | 'luxury' | 'custom';
}

interface HotelPageBuilderProps {
    hotelId: string;
    language?: 'en' | 'sv';
}

const WIDGET_TYPES = [
    { type: 'hero', icon: 'image', label: { en: 'Hero Banner', sv: 'Hjältebild' } },
    { type: 'gallery', icon: 'collections', label: { en: 'Image Gallery', sv: 'Bildgalleri' } },
    { type: 'video', icon: 'play_circle', label: { en: 'Video Section', sv: 'Videosektion' } },
    { type: 'description', icon: 'description', label: { en: 'Description', sv: 'Beskrivning' } },
    { type: 'facilities', icon: 'fitness_center', label: { en: 'Facilities', sv: 'Faciliteter' } },
    { type: 'rooms', icon: 'bed', label: { en: 'Room Types', sv: 'Rumstyper' } },
    { type: 'map', icon: 'map', label: { en: 'Location Map', sv: 'Karta' } },
    { type: 'reviews', icon: 'star', label: { en: 'Reviews', sv: 'Omdömen' } },
    { type: 'pricing', icon: 'payments', label: { en: 'Pricing', sv: 'Priser' } },
    { type: 'booking', icon: 'book_online', label: { en: 'Booking Form', sv: 'Bokningsformulär' } },
    { type: 'virtual_tour', icon: '360', label: { en: '3D Tour', sv: '3D-tur' } },
    { type: 'instagram_feed', icon: 'photo_camera', label: { en: 'Instagram Feed', sv: 'Instagram-flöde' } },
    { type: 'weather', icon: 'wb_sunny', label: { en: 'Weather Widget', sv: 'Väderwidget' } },
    { type: 'custom_html', icon: 'code', label: { en: 'Custom HTML', sv: 'Anpassad HTML' } },
];

const THEME_PRESETS = [
    { id: 'default', name: { en: 'Default', sv: 'Standard' }, primary: '#0ea5e9', accent: '#f59e0b' },
    { id: 'minimal', name: { en: 'Minimal', sv: 'Minimal' }, primary: '#374151', accent: '#10b981' },
    { id: 'luxury', name: { en: 'Luxury', sv: 'Lyx' }, primary: '#1e1b4b', accent: '#c9a227' },
    { id: 'ocean', name: { en: 'Ocean', sv: 'Hav' }, primary: '#0d9488', accent: '#06b6d4' },
];

const HotelPageBuilder: React.FC<HotelPageBuilderProps> = ({ hotelId, language = 'en' }) => {
    const [widgets, setWidgets] = useState<Widget[]>([]);
    const [theme, setTheme] = useState<PageTheme>({
        primaryColor: '#0ea5e9',
        accentColor: '#f59e0b',
        font: 'Inter',
        layout: 'default'
    });
    const [customCss, setCustomCss] = useState('');
    const [useCustomLayout, setUseCustomLayout] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
    const [showAddWidget, setShowAddWidget] = useState(false);
    const [editingWidget, setEditingWidget] = useState<string | null>(null);

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    const fetchLayout = async () => {
        try {
            const res = await fetch(`/api/hotels/${hotelId}/widgets`);
            const data = await res.json();
            if (data.success) {
                setWidgets(data.pageLayout.widgets || []);
                setTheme(data.pageLayout.theme || theme);
                setCustomCss(data.pageLayout.customCss || '');
                setUseCustomLayout(data.pageLayout.useCustomLayout || false);
            }
        } catch (error) {
            console.error('Error fetching layout:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchLayout();
    }, [hotelId]);

    const saveLayout = async () => {
        setSaving(true);
        try {
            const res = await fetch(`/api/hotels/${hotelId}/widgets`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ widgets, theme, customCss, useCustomLayout })
            });
            const data = await res.json();
            if (data.success) {
                showMessage('success', language === 'sv' ? 'Layout sparad!' : 'Layout saved!');
            } else {
                showMessage('error', data.error);
            }
        } catch (error) {
            showMessage('error', 'Failed to save layout');
        }
        setSaving(false);
    };

    const toggleWidgetVisibility = (widgetId: string) => {
        setWidgets(widgets.map(w =>
            w.id === widgetId ? { ...w, isVisible: !w.isVisible } : w
        ));
    };

    const removeWidget = (widgetId: string) => {
        setWidgets(widgets.filter(w => w.id !== widgetId));
    };

    const addWidget = (type: string) => {
        const newWidget: Widget = {
            id: `${type}-${Date.now()}`,
            type,
            order: widgets.length,
            isVisible: true,
            settings: {}
        };
        setWidgets([...widgets, newWidget]);
        setShowAddWidget(false);
    };

    const updateWidgetSettings = (widgetId: string, newSettings: Record<string, any>) => {
        setWidgets(widgets.map(w =>
            w.id === widgetId ? { ...w, settings: { ...w.settings, ...newSettings } } : w
        ));
    };

    const getWidgetSettingsFields = (type: string) => {
        switch (type) {
            case 'hero':
                return [
                    { key: 'height', label: { en: 'Height', sv: 'Höjd' }, type: 'select', options: ['small', 'medium', 'large', 'full'] },
                    { key: 'overlay', label: { en: 'Overlay', sv: 'Överlägg' }, type: 'checkbox' },
                    { key: 'title', label: { en: 'Custom Title', sv: 'Anpassad titel' }, type: 'text' },
                ];
            case 'gallery':
                return [
                    { key: 'columns', label: { en: 'Columns', sv: 'Kolumner' }, type: 'select', options: ['2', '3', '4', '5'] },
                    { key: 'style', label: { en: 'Style', sv: 'Stil' }, type: 'select', options: ['grid', 'masonry', 'carousel'] },
                    { key: 'showCaptions', label: { en: 'Show Captions', sv: 'Visa bildtexter' }, type: 'checkbox' },
                ];
            case 'description':
                return [
                    { key: 'showSwedish', label: { en: 'Show Swedish', sv: 'Visa svenska' }, type: 'checkbox' },
                    { key: 'maxLength', label: { en: 'Max Length', sv: 'Max längd' }, type: 'number' },
                ];
            case 'facilities':
                return [
                    { key: 'layout', label: { en: 'Layout', sv: 'Layout' }, type: 'select', options: ['grid', 'list', 'icons'] },
                    { key: 'showIcons', label: { en: 'Show Icons', sv: 'Visa ikoner' }, type: 'checkbox' },
                ];
            case 'rooms':
                return [
                    { key: 'showPrices', label: { en: 'Show Prices', sv: 'Visa priser' }, type: 'checkbox' },
                    { key: 'showImages', label: { en: 'Show Images', sv: 'Visa bilder' }, type: 'checkbox' },
                ];
            case 'map':
                return [
                    { key: 'height', label: { en: 'Height (px)', sv: 'Höjd (px)' }, type: 'number' },
                    { key: 'zoom', label: { en: 'Zoom Level', sv: 'Zoomnivå' }, type: 'number' },
                ];
            case 'video':
                return [
                    { key: 'url', label: { en: 'Video URL', sv: 'Video-URL' }, type: 'text' },
                    { key: 'autoplay', label: { en: 'Autoplay', sv: 'Autospela' }, type: 'checkbox' },
                ];
            case 'instagram_feed':
                return [
                    { key: 'username', label: { en: 'Username', sv: 'Användarnamn' }, type: 'text' },
                    { key: 'count', label: { en: 'Post Count', sv: 'Antal inlägg' }, type: 'number' },
                ];
            case 'custom_html':
                return [
                    { key: 'html', label: { en: 'HTML Code', sv: 'HTML-kod' }, type: 'textarea' },
                ];
            default:
                return [];
        }
    };

    const handleDragStart = (widgetId: string) => {
        setDraggedWidget(widgetId);
    };

    const handleDragOver = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedWidget || draggedWidget === targetId) return;

        const draggedIndex = widgets.findIndex(w => w.id === draggedWidget);
        const targetIndex = widgets.findIndex(w => w.id === targetId);

        const newWidgets = [...widgets];
        const [removed] = newWidgets.splice(draggedIndex, 1);
        newWidgets.splice(targetIndex, 0, removed);

        setWidgets(newWidgets.map((w, i) => ({ ...w, order: i })));
    };

    const handleDragEnd = () => {
        setDraggedWidget(null);
    };

    const applyThemePreset = (preset: typeof THEME_PRESETS[0]) => {
        setTheme({
            ...theme,
            primaryColor: preset.primary,
            accentColor: preset.accent,
            layout: preset.id as any
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Message Toast */}
            {message && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${message.type === 'success' ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                    {message.text}
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-white">
                        {language === 'sv' ? 'Sidbyggare' : 'Page Builder'}
                    </h3>
                    <p className="text-sm text-gray-400">
                        {language === 'sv' ? 'Anpassa hotellsidans layout och widgets' : 'Customize hotel page layout and widgets'}
                    </p>
                </div>
                <div className="flex gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-400">
                        <input
                            type="checkbox"
                            checked={useCustomLayout}
                            onChange={(e) => setUseCustomLayout(e.target.checked)}
                            className="rounded bg-slate-700 border-slate-600"
                        />
                        {language === 'sv' ? 'Använd anpassad layout' : 'Use custom layout'}
                    </label>
                    <button
                        onClick={saveLayout}
                        disabled={saving}
                        className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium flex items-center gap-2"
                    >
                        <span className="material-icons text-lg">{saving ? 'sync' : 'save'}</span>
                        {saving ? (language === 'sv' ? 'Sparar...' : 'Saving...') : (language === 'sv' ? 'Spara' : 'Save')}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Widget List */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-400">
                            {language === 'sv' ? 'Widgets (dra för att ordna om)' : 'Widgets (drag to reorder)'}
                        </h4>
                        <button
                            onClick={() => setShowAddWidget(!showAddWidget)}
                            className="px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm flex items-center gap-1"
                        >
                            <span className="material-icons text-lg">add</span>
                            {language === 'sv' ? 'Lägg till widget' : 'Add Widget'}
                        </button>
                    </div>

                    {/* Add Widget Panel */}
                    {showAddWidget && (
                        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                            <p className="text-sm text-gray-400 mb-3">{language === 'sv' ? 'Välj widget att lägga till:' : 'Select widget to add:'}</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {WIDGET_TYPES.map((wt) => (
                                    <button
                                        key={wt.type}
                                        onClick={() => addWidget(wt.type)}
                                        className="p-3 bg-slate-700 rounded-lg hover:bg-slate-600 text-left transition-colors"
                                    >
                                        <span className="material-icons text-teal-400 text-xl mb-1">{wt.icon}</span>
                                        <p className="text-sm text-white">{wt.label[language]}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Widget Items */}
                    <div className="space-y-2">
                        {widgets.sort((a, b) => a.order - b.order).map((widget) => {
                            const widgetType = WIDGET_TYPES.find(wt => wt.type === widget.type);
                            return (
                                <div
                                    key={widget.id}
                                    draggable
                                    onDragStart={() => handleDragStart(widget.id)}
                                    onDragOver={(e) => handleDragOver(e, widget.id)}
                                    onDragEnd={handleDragEnd}
                                    className={`bg-slate-800 rounded-xl p-4 border ${widget.isVisible ? 'border-slate-700' : 'border-slate-600 opacity-50'} ${draggedWidget === widget.id ? 'ring-2 ring-teal-500' : ''} cursor-move transition-all`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="material-icons text-gray-500 cursor-grab">drag_indicator</span>
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${widget.isVisible ? 'bg-teal-600/20 text-teal-400' : 'bg-slate-700 text-gray-500'}`}>
                                                <span className="material-icons">{widgetType?.icon || 'widgets'}</span>
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">{widgetType?.label[language] || widget.type}</p>
                                                <p className="text-xs text-gray-500">ID: {widget.id}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => toggleWidgetVisibility(widget.id)}
                                                className={`p-2 rounded-lg ${widget.isVisible ? 'bg-green-600/20 text-green-400' : 'bg-slate-700 text-gray-500'}`}
                                                title={widget.isVisible ? 'Hide' : 'Show'}
                                            >
                                                <span className="material-icons text-lg">{widget.isVisible ? 'visibility' : 'visibility_off'}</span>
                                            </button>
                                            <button
                                                onClick={() => setEditingWidget(editingWidget === widget.id ? null : widget.id)}
                                                className={`p-2 rounded-lg ${editingWidget === widget.id ? 'bg-blue-600 text-white' : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'}`}
                                                title={language === 'sv' ? 'Redigera' : 'Edit'}
                                            >
                                                <span className="material-icons text-lg">edit</span>
                                            </button>
                                            <button
                                                onClick={() => removeWidget(widget.id)}
                                                className="p-2 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30"
                                                title="Remove"
                                            >
                                                <span className="material-icons text-lg">delete</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Widget Settings Panel */}
                                    {editingWidget === widget.id && (
                                        <div className="mt-4 pt-4 border-t border-slate-600">
                                            <h5 className="text-sm font-medium text-gray-300 mb-3">
                                                {language === 'sv' ? 'Inställningar' : 'Settings'}
                                            </h5>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {getWidgetSettingsFields(widget.type).map((field) => (
                                                    <div key={field.key} className="space-y-1">
                                                        <label className="text-xs text-gray-400">{field.label[language]}</label>
                                                        {field.type === 'text' && (
                                                            <input
                                                                type="text"
                                                                value={widget.settings[field.key] || ''}
                                                                onChange={(e) => updateWidgetSettings(widget.id, { [field.key]: e.target.value })}
                                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                                                            />
                                                        )}
                                                        {field.type === 'number' && (
                                                            <input
                                                                type="number"
                                                                value={widget.settings[field.key] || ''}
                                                                onChange={(e) => updateWidgetSettings(widget.id, { [field.key]: parseInt(e.target.value) || 0 })}
                                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                                                            />
                                                        )}
                                                        {field.type === 'checkbox' && (
                                                            <label className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={widget.settings[field.key] || false}
                                                                    onChange={(e) => updateWidgetSettings(widget.id, { [field.key]: e.target.checked })}
                                                                    className="rounded bg-slate-700 border-slate-600"
                                                                />
                                                                <span className="text-sm text-gray-300">{language === 'sv' ? 'Aktiverad' : 'Enabled'}</span>
                                                            </label>
                                                        )}
                                                        {field.type === 'select' && (
                                                            <select
                                                                value={widget.settings[field.key] || ''}
                                                                onChange={(e) => updateWidgetSettings(widget.id, { [field.key]: e.target.value })}
                                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                                                            >
                                                                <option value="">{language === 'sv' ? 'Välj...' : 'Select...'}</option>
                                                                {field.options?.map((opt: string) => (
                                                                    <option key={opt} value={opt}>{opt}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        {field.type === 'textarea' && (
                                                            <textarea
                                                                value={widget.settings[field.key] || ''}
                                                                onChange={(e) => updateWidgetSettings(widget.id, { [field.key]: e.target.value })}
                                                                rows={4}
                                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono resize-none"
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            {getWidgetSettingsFields(widget.type).length === 0 && (
                                                <p className="text-sm text-gray-500 italic">
                                                    {language === 'sv' ? 'Inga inställningar tillgängliga för denna widget.' : 'No settings available for this widget.'}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Theme Panel */}
                <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-400">{language === 'sv' ? 'Tema' : 'Theme'}</h4>

                    {/* Theme Presets */}
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <p className="text-xs text-gray-500 mb-3">{language === 'sv' ? 'Förinställningar' : 'Presets'}</p>
                        <div className="grid grid-cols-2 gap-2">
                            {THEME_PRESETS.map((preset) => (
                                <button
                                    key={preset.id}
                                    onClick={() => applyThemePreset(preset)}
                                    className={`p-3 rounded-lg border ${theme.layout === preset.id ? 'border-teal-500 bg-teal-600/10' : 'border-slate-600 hover:border-slate-500'} transition-colors`}
                                >
                                    <div className="flex gap-2 mb-2">
                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }}></div>
                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }}></div>
                                    </div>
                                    <p className="text-sm text-white">{preset.name[language]}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Colors */}
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 space-y-3">
                        <p className="text-xs text-gray-500">{language === 'sv' ? 'Anpassade färger' : 'Custom Colors'}</p>
                        <div className="flex items-center gap-3">
                            <label className="text-sm text-gray-400 w-20">{language === 'sv' ? 'Primär' : 'Primary'}</label>
                            <input
                                type="color"
                                value={theme.primaryColor}
                                onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                                className="w-10 h-10 rounded cursor-pointer"
                            />
                            <input
                                type="text"
                                value={theme.primaryColor}
                                onChange={(e) => setTheme({ ...theme, primaryColor: e.target.value })}
                                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="text-sm text-gray-400 w-20">{language === 'sv' ? 'Accent' : 'Accent'}</label>
                            <input
                                type="color"
                                value={theme.accentColor}
                                onChange={(e) => setTheme({ ...theme, accentColor: e.target.value })}
                                className="w-10 h-10 rounded cursor-pointer"
                            />
                            <input
                                type="text"
                                value={theme.accentColor}
                                onChange={(e) => setTheme({ ...theme, accentColor: e.target.value })}
                                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                            />
                        </div>
                    </div>

                    {/* Custom CSS */}
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <p className="text-xs text-gray-500 mb-2">{language === 'sv' ? 'Anpassad CSS' : 'Custom CSS'}</p>
                        <textarea
                            value={customCss}
                            onChange={(e) => setCustomCss(e.target.value)}
                            placeholder=".hotel-page { ... }"
                            rows={4}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono placeholder-gray-500 resize-none"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HotelPageBuilder;
